import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../firebase';
import { doc, getDoc, getDocs, collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import SuperAdminSchoolsView from './SuperAdminSchoolsView';
import SuperAdminActivityLogsView from './SuperAdminActivityLogsView';
import SuperAdminClassesView from './SuperAdminClassesView';
import SuperAdminStudentsView from './SuperAdminStudentsView';
import SuperAdminUsersView from './SuperAdminUsersView';
import SuperAdminParentsView from './SuperAdminParentsView';
import SuperAdminEventsView from './SuperAdminEventsView';
import SuperAdminGradesView from './SuperAdminGradesView';
import SuperAdminPerformanceView from './SuperAdminPerformanceView';
import SuperAdminApprovalsView from './SuperAdminApprovalsView';
import SuperAdminReportsView from './SuperAdminReportsView';
import SuperAdminStudentReportsView from './SuperAdminStudentReportsView';
import SuperAdminTeacherPrepView from './SuperAdminTeacherPrepView';
import SuperAdminGradingView from './SuperAdminGradingView';
import SuperAdminHonorRollView from './SuperAdminHonorRollView';
import SuperAdminSettingsView from './SuperAdminSettingsView';
import SuperAdminAnalyticsView from './SuperAdminAnalyticsView';
import SuperAdminStoreView from './SuperAdminStoreView';
import SuperAdminTimetableView from './SuperAdminTimetableView';
import SuperAdminProfileView from './SuperAdminProfileView';
import SuperAdminMessagesView from './SuperAdminMessagesView';
import SuperAdminBehaviorView from './SuperAdminBehaviorView';
import SuperAdminOnlineClassesView from './SuperAdminOnlineClassesView';
import SuperAdminTeachersView from './SuperAdminTeachersView';
import SuperAdminNotificationsView from './SuperAdminNotificationsView';
import './SuperAdminDashboard.css';

function SuperAdminDashboard() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSchoolId, setSelectedSchoolId] = useState(null); // null = national view
  
  // Batch counts for admin tab badges
  const [badgeCounts, setBadgeCounts] = useState({
    approvals: 0,
    storeOrders: 0,
    newStudents: 0,
    newParents: 0
  });
  // Live totals + what the admin has already seen — badge shows live minus baseline
  const liveCountsRef = useRef({});
  const badgeBaselineRef = useRef({});

  const superAdminTabs = [
    { id: 0, title: 'Overview', icon: '📊' },
    { id: 1, title: 'Schools', icon: '🏫' },
    { id: 2, title: 'Users', icon: '👤' },
    { id: 3, title: 'Students', icon: '👥' },
    { id: 4, title: 'Classes', icon: '📚' },
    { id: 5, title: 'Grades', icon: '🎓' },
    { id: 6, title: 'Performance', icon: '📈' },
    { id: 7, title: 'Approvals', icon: '✅' },
    { id: 8, title: 'Reports', icon: '📄' },
    { id: 9, title: 'Teacher Prep', icon: '📖' },
    { id: 10, title: 'Parents', icon: '👨‍👩‍👧' },
    { id: 11, title: 'Events', icon: '📅' },
    { id: 12, title: 'Grading', icon: '📝' },
    { id: 13, title: 'Honor Roll', icon: '⭐' },
    { id: 14, title: 'Store', icon: '🛍️' },
    { id: 15, title: 'Timetable', icon: '🗓️' },
    { id: 16, title: 'Activity Logs', icon: '📋' },
    { id: 17, title: 'Analytics', icon: '📊' },
    { id: 18, title: 'Settings', icon: '⚙️' },
    { id: 19, title: 'Profile', icon: '👤' },
    { id: 20, title: 'Student Reports', icon: '🚨' },
    { id: 21, title: 'Messages', icon: '💬' },
    { id: 22, title: 'Online Classes', icon: '📹' },
    { id: 23, title: 'Behavior', icon: '📋' },
    { id: 24, title: 'Teachers', icon: '👨‍🏫' },
    { id: 25, title: 'Notifications', icon: '🔔' }
  ];

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const userData = userDoc.exists() ? userDoc.data() : {};
          setAdminData({
            uid: user.uid,
            uuid: userData.uuid || null,
            email: user.email,
            displayName: userData.username || user.displayName || user.email.split('@')[0],
            role: userData.role?.toLowerCase() || 'super admin',
            schoolId: userData.schoolId || null
          });
        } catch (error) {
          console.error('Error loading admin data:', error);
          setAdminData({
            uid: user.uid,
            uuid: null,
            email: user.email,
            displayName: user.displayName || user.email.split('@')[0],
            role: 'super admin',
            schoolId: null
          });
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Live badge counts — onSnapshot listeners so counts update the instant data changes
  useEffect(() => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    // Display = live total minus what the admin has already seen (baseline)
    const updateCount = (key, live) => {
      liveCountsRef.current = { ...liveCountsRef.current, [key]: live };
      const visible = Math.max(0, live - (badgeBaselineRef.current[key] || 0));
      setBadgeCounts(prev => ({ ...prev, [key]: visible }));
    };

    const unsubs = [
      onSnapshot(collection(db, 'gradeChangeRequests'), snap => {
        const n = snap.docs.filter(d =>
          String(d.data().status || 'pending').toLowerCase() === 'pending'
        ).length;
        updateCount('approvals', n);
      }, err => console.error('🔥 Badge listener (gradeChangeRequests):', err)),

      onSnapshot(collection(db, 'storeOrders'), snap => {
        const n = snap.docs.filter(d =>
          String(d.data().status || 'pending').toLowerCase() === 'pending'
        ).length;
        updateCount('storeOrders', n);
      }, err => console.error('🔥 Badge listener (storeOrders):', err)),

      onSnapshot(collection(db, 'students'), snap => {
        const n = snap.docs.filter(d =>
          d.data().createdAt && new Date(d.data().createdAt) >= sevenDaysAgo
        ).length;
        updateCount('newStudents', n);
      }, err => console.error('🔥 Badge listener (students):', err)),

      onSnapshot(collection(db, 'parents'), snap => {
        const n = snap.docs.filter(d =>
          d.data().createdAt && new Date(d.data().createdAt) >= sevenDaysAgo
        ).length;
        updateCount('newParents', n);
      }, err => console.error('🔥 Badge listener (parents):', err))
    ];

    return () => unsubs.forEach(u => u());
  }, []);

  // Clear the badge for the tab being opened — baseline = current live count,
  // so only genuinely new items bump it again
  const clearBadgeForTab = (tabId) => {
    const key = { 7: 'approvals', 14: 'storeOrders' }[tabId];
    if (key) {
      badgeBaselineRef.current = {
        ...badgeBaselineRef.current,
        [key]: liveCountsRef.current[key] || 0
      };
      setBadgeCounts(prev => ({ ...prev, [key]: 0 }));
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }

  const renderTabContent = () => {
    switch (selectedTab) {
      case 0: // Overview
        return <SuperAdminOverviewView selectedSchoolId={selectedSchoolId} setSelectedSchoolId={setSelectedSchoolId} />;
      case 1: // Schools
        return <SuperAdminSchoolsView />;
      case 2: // Users
        return <SuperAdminUsersView adminData={adminData} />;
      case 3: // Students
        return <SuperAdminStudentsView adminData={adminData} />;
      case 4: // Classes
        return <SuperAdminClassesView adminData={adminData} />;
      case 5: // Grades
        return <SuperAdminGradesView adminData={adminData} />;
      case 6: // Performance
        return <SuperAdminPerformanceView adminData={adminData} />;
      case 7: // Approvals
        return <SuperAdminApprovalsView adminData={adminData} />;
      case 8: // Reports
        return <SuperAdminReportsView adminData={adminData} />;
      case 9: // Teacher Prep
        return <SuperAdminTeacherPrepView adminData={adminData} />;
      case 10: // Parents
        return <SuperAdminParentsView adminData={adminData} />;
      case 11: // Events
        return <SuperAdminEventsView adminData={adminData} />;
      case 12: // Grading
        return <SuperAdminGradingView adminData={adminData} />;
      case 13: // Honor Roll
        return <SuperAdminHonorRollView adminData={adminData} />;
      case 14: // Store
        return <SuperAdminStoreView adminData={adminData} />;
      case 15: // Timetable
        return <SuperAdminTimetableView schoolId={selectedSchoolId} />;
      case 16: // Activity Logs
        return <SuperAdminActivityLogsView />;
      case 17: // Analytics
        return <SuperAdminAnalyticsView adminData={adminData} />;
      case 18: // Settings
        return <SuperAdminSettingsView adminData={adminData} />;
      case 19: // Profile
        return <SuperAdminProfileView adminData={adminData} />;
      case 20: // Student Reports
        return <SuperAdminStudentReportsView adminData={adminData} />;
      case 21: // Messages
        return <SuperAdminMessagesView adminData={adminData} />;
      case 22: // Online Classes
        return <SuperAdminOnlineClassesView />;
      case 23: // Behavior
        return adminData ? <SuperAdminBehaviorView user={adminData} /> : null;
      case 24: // Teachers
        return <SuperAdminTeachersView adminData={adminData} />;
      case 25: // Notifications
        return <SuperAdminNotificationsView adminData={adminData} />;
      default:
        return <div>Tab not implemented</div>;
    }
  };

  return (
    <div className="super-admin-dashboard">
      <div className="dashboard-header">
        <div className="header-left">
          <h1>KN Education Portal</h1>
          <p className="subtitle">Ministry of Education</p>
        </div>
        <div className="user-info">
          <span className="role-badge">Super Admin</span>
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="sidebar">
          {superAdminTabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${selectedTab === tab.id ? 'active' : ''}`}
              onClick={() => { setSelectedTab(tab.id); clearBadgeForTab(tab.id); }}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-title">{tab.title}</span>
              {tab.id === 7 && badgeCounts.approvals > 0 && (
                <span className="badge">{badgeCounts.approvals}</span>
              )}
              {tab.id === 14 && badgeCounts.storeOrders > 0 && (
                <span className="badge">{badgeCounts.storeOrders}</span>
              )}
            </button>
          ))}
        </div>

        <div className="main-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}

// National Overview View
function SuperAdminOverviewView({ selectedSchoolId, setSelectedSchoolId }) {
  const [stats, setStats] = useState({
    totalSchools: 0,
    totalStudents: 0,
    totalTeachers: 0,
    totalParents: 0,
    totalClasses: 0,
    totalEvents: 0
  });
  const [schoolsList, setSchoolsList] = useState([]);
  const [schoolComparison, setSchoolComparison] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadNationalStats = useCallback(async () => {
    try {
      const [schools, students, users, classes, events] = await Promise.all([
        getDocs(collection(db, 'schools')),
        getDocs(collection(db, 'students')),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'classes')),
        getDocs(collection(db, 'events'))
      ]);

      const schoolsData = schools.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSchoolsList(schoolsData);

      const teachers = users.docs.filter(doc => doc.data().role === 'Teacher').length;
      const parents = users.docs.filter(doc => doc.data().role === 'Parent').length;

      setStats({
        totalSchools: schools.docs.length,
        totalStudents: students.docs.length,
        totalTeachers: teachers,
        totalParents: parents,
        totalClasses: classes.docs.length,
        totalEvents: events.docs.length
      });

      // Build per-school comparison data
      const classesData = classes.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const usersData = users.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const studentsData = students.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const comparison = schoolsData.map(school => {
        const schoolClasses = classesData.filter(c => c.schoolId === school.id);
        const schoolTeachers = usersData.filter(u => u.role === 'Teacher' && u.schoolId === school.id);
        const schoolStudents = studentsData.filter(s => s.schoolId === school.id);
        return {
          name: school.name || 'Unknown',
          students: schoolStudents.length,
          teachers: schoolTeachers.length,
          classes: schoolClasses.length,
        };
      }).sort((a, b) => b.students - a.students);
      setSchoolComparison(comparison);
    } catch (error) {
      console.error('Error loading national stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNationalStats();
  }, [loadNationalStats]);

  if (loading) {
    return <div className="loading-container">Loading national data...</div>;
  }

  const userDistributionData = [
    { name: 'Students', value: stats.totalStudents, color: '#22c55e' },
    { name: 'Teachers', value: stats.totalTeachers, color: '#f59e0b' },
    { name: 'Parents', value: stats.totalParents, color: '#8b5cf6' },
  ].filter(d => d.value > 0);

  return (
    <div className="national-overview">
      <div className="overview-header">
        <h2>National Overview</h2>
        <div className="school-selector">
          <label>Viewing: </label>
          <select
            value={selectedSchoolId || 'national'}
            onChange={(e) => setSelectedSchoolId(e.target.value === 'national' ? null : e.target.value)}
          >
            <option value="national">All Schools (National)</option>
            {schoolsList.map(school => (
              <option key={school.id} value={school.id}>{school.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="stats-grid">
        <StatCard title="Total Schools" value={stats.totalSchools} icon="🏫" color="blue" />
        <StatCard title="Total Students" value={stats.totalStudents} icon="👥" color="green" />
        <StatCard title="Total Teachers" value={stats.totalTeachers} icon="👨‍🏫" color="orange" />
        <StatCard title="Total Parents" value={stats.totalParents} icon="👨‍👩‍👧" color="purple" />
        <StatCard title="Total Classes" value={stats.totalClasses} icon="📚" color="red" />
        <StatCard title="Total Events" value={stats.totalEvents} icon="📅" color="pink" />
      </div>

      {/* Charts Section */}
      <div className="overview-charts">
        {userDistributionData.length > 0 && (
          <div className="chart-card">
            <h3>User Distribution</h3>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={userDistributionData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100}>
                  {userDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend formatter={(value, entry) => `${value} (${entry.payload?.value ?? 0})`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {schoolComparison.length > 0 && (
          <div className="chart-card">
            <h3>Students by School</h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={schoolComparison} margin={{ top: 20, right: 30, left: 0, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-35} textAnchor="end" height={80} interval={0} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="students" fill="#22c55e" radius={[4, 4, 0, 0]} name="Students" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {schoolComparison.length > 0 && (
          <div className="chart-card">
            <h3>Teachers by School</h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={schoolComparison} margin={{ top: 20, right: 30, left: 0, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-35} textAnchor="end" height={80} interval={0} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="teachers" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Teachers" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {schoolComparison.length > 0 && (
          <div className="chart-card">
            <h3>Classes by School</h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={schoolComparison} margin={{ top: 20, right: 30, left: 0, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-35} textAnchor="end" height={80} interval={0} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="classes" fill="#22c55e" radius={[4, 4, 0, 0]} name="Classes" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="info-card">
        <h3>Ministry of Education — Saint Kitts & Nevis</h3>
        <p>National Education Management System</p>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }) {
  return (
    <div className={`stat-card stat-card-${color}`}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-content">
        <div className="stat-value">{value}</div>
        <div className="stat-title">{title}</div>
      </div>
    </div>
  );
}

export default SuperAdminDashboard;
